import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './GuestCheckIn.css';
import { API_BASE_URL, SERVICE_BASE_URL as SERVICE_BASE } from '../utils/apiBase';
import SuccessModal from './SuccessModal';

const IS_DEV = process.env.NODE_ENV === 'development';
const IS_VNEXT = process.env.REACT_APP_VNEXT === 'true';

function HealthBadge({ apiBase }) {
  const [latency, setLatency] = useState(null);
  const [dbLatency, setDbLatency] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | ok | fail

  useEffect(() => {
    if (!IS_VNEXT) return;
    let cancelled = false;

    const ping = async () => {
      const start = performance.now();
      try {
        setStatus('ping');
        const res = await fetch(`${apiBase}/health`);
        if (!res.ok) throw new Error('Health check failed');
        const end = performance.now();
        const data = await res.json().catch(() => null);
        if (!cancelled) {
          setLatency(Math.round(end - start));
          setDbLatency(data?.dbLatency || null);
          setStatus('ok');
        }
      } catch {
        if (!cancelled) {
          setLatency(null);
          setDbLatency(null);
          setStatus('fail');
        }
      }
    };

    ping();
    const id = setInterval(ping, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [apiBase]);

  if (!IS_VNEXT) return null;

  const color = status === 'ok' ? '#198754' : status === 'fail' ? '#dc3545' : '#6c757d';
  return (
    <div
      style={{
        position: 'absolute',
        top: 8,
        right: 8,
        fontSize: '0.55rem',
        background: color,
        color: '#fff',
        padding: '4px 6px',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        flexWrap: 'wrap',
        maxWidth: 140,
      }}
      title={status === 'ok' && latency ? `API ${latency}ms${dbLatency != null ? ` • DB ${dbLatency}ms` : ''}` : 'API health'}
    >
      <span>{status === 'ok' ? 'API OK' : status === 'fail' ? 'API DOWN' : 'PING'}</span>
      {latency != null && <span style={{ opacity: 0.8 }}>{latency}ms</span>}
      {dbLatency != null && <span style={{ opacity: 0.8 }}>DB {dbLatency}ms</span>}
    </div>
  );
}

export default function GuestCheckIn({ eventId = null, eventConfig: incomingEventConfig = null }) {
  const [lastName, setLastName] = useState('');
  const [type, setType] = useState('Single');
  const [groupSize, setGroupSize] = useState(3);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [loading, setLoading] = useState(false);
  const [multipleGuests, setMultipleGuests] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [showGuestSelection, setShowGuestSelection] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [seatInfo, setSeatInfo] = useState(null);
  const [eventConfig, setEventConfig] = useState(incomingEventConfig);
  const [showCheckSeat, setShowCheckSeat] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [menuServiceEnabled, setMenuServiceEnabled] = useState(false);
  const [selectionMode, setSelectionMode] = useState(null);
  const [successMode, setSuccessMode] = useState(null);
  const [menuOptions, setMenuOptions] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuError, setMenuError] = useState('');
  const [menuSelections, setMenuSelections] = useState([]);
  const [pendingManageToken, setPendingManageToken] = useState(null);
  const [waitlistInfo, setWaitlistInfo] = useState(null);
  const [pollWaitlist, setPollWaitlist] = useState(false);
  const [restoringSession, setRestoringSession] = useState(false);
  const [restoredFromSession, setRestoredFromSession] = useState(false);
  const [uiModeInitialized, setUiModeInitialized] = useState(false);

  // Global error capture
  useEffect(() => {
    const onError = (e) => {
      try {
        window.__guestErrors = window.__guestErrors || [];
        window.__guestErrors.push({ t: Date.now(), message: e.message, stack: e.error?.stack || null });
        if (!document.getElementById('guest-error-indicator')) {
          const el = document.createElement('div');
          el.id = 'guest-error-indicator';
          el.style.cssText =
            'position:fixed;bottom:4px;right:4px;background:#dc3545;color:#fff;font:10px/1.1 monospace;padding:4px 6px;border-radius:4px;z-index:9999;';
          el.textContent = 'ERR';
          document.body.appendChild(el);
        }
      } catch {}
    };
    window.addEventListener('error', onError);
    return () => window.removeEventListener('error', onError);
  }, []);

  // Log lastName changes
  useEffect(() => {
    try {
      window.__guestDebug = window.__guestDebug || [];
      window.__guestDebug.push({ t: Date.now(), lastName, len: lastName.length });
    } catch {}
  }, [lastName]);

  // Initialize UI mode
  useEffect(() => {
    setUiModeInitialized(true);
  }, []);

  // Fetch menu service status
  useEffect(() => {
    let cancelled = false;
    const rootBase = API_BASE_URL.endsWith('/api') ? API_BASE_URL.slice(0, -4) : API_BASE_URL;

    const attemptFetch = async (url) => {
      try {
        const r = await fetch(url);
        return r.ok ? { ok: true, status: r.status, data: await r.json() } : { ok: false, status: r.status };
      } catch (e) {
        return { ok: false, error: e.message };
      }
    };

    const fetchStatus = async () => {
      const eventIdParam = eventId ? `?event_id=${encodeURIComponent(eventId)}` : '';
      const primary = `${SERVICE_BASE}/menu/status${eventIdParam}`;
      const fallback = `${rootBase}/menu/status${eventIdParam}`;
      let res = await attemptFetch(primary);
      if (!res.ok) {
        const res2 = await attemptFetch(fallback);
        if (res2.ok) res = { ...res2, fromFallback: true };
      }
      if (res.ok && !cancelled) {
        setMenuServiceEnabled(!!res.data.enabled);
        if (res.data.enabled) {
          setMenuLoading(true);
          setMenuError('');
          const optPrimary = `${SERVICE_BASE}/menu/options/active${eventIdParam}`;
          const optFallback = `${rootBase}/menu/options/active${eventIdParam}`;
          let oRes = await attemptFetch(optPrimary);
          if (!oRes.ok) {
            const oRes2 = await attemptFetch(optFallback);
            if (oRes2.ok) oRes = { ...oRes2, fromFallback: true };
          }
          if (oRes.ok && !cancelled) {
            setMenuOptions(Array.isArray(oRes.data) ? oRes.data : []);
          } else if (!cancelled) {
            setMenuError('Failed to load menu');
          }
          setMenuLoading(false);
        }
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 300000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [SERVICE_BASE, API_BASE_URL, eventId]);

  // Load event configuration and resolve tokens
  useEffect(() => {
    const loadEventConfig = async () => {
      if (incomingEventConfig) return;
      try {
        const response = await axios.get(`${API_BASE_URL}/event-config`);
        setEventConfig(response.data);
      } catch {
        setEventConfig({ eventTitle: 'Event Check-in', eventLogo: null, poweredBy: 'Guest Placement System' });
      }
    };

    const prefillFromToken = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('t');
        if (!token) return;
        const { data } = await axios.get(`${API_BASE_URL}/qr/resolve`, { params: { token } });
        if (data?.lastName) setLastName(data.lastName);
        if (data?.type) setType(data.type);
        if (data?.action === 'checkin') await handleCheckIn(token);
      } catch {
        // Silent fail for token resolution
      }
    };

    const attemptManageLookup = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const guestToken = params.get('guestToken');
        if (guestToken) setPendingManageToken(guestToken);
      } catch {}
    };

    loadEventConfig();
    prefillFromToken();
    attemptManageLookup();
  }, [incomingEventConfig, API_BASE_URL, eventId]);

  // Session restore
  useEffect(() => {
    if (!IS_VNEXT || showSuccess || restoredFromSession) return;
    let cancelled = false;
    let token = null;
    try {
      token = localStorage.getItem('gp_guest_session_token');
    } catch {
      token = null;
    }
    if (!token) return;

    (async () => {
      setRestoringSession(true);
      try {
        const { data } = await axios.get(`${API_BASE_URL}/public/session/${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (data?.guest && data.table) {
          const seatData = {
            table: data.table,
            seat: data.seat,
            seatNumbers: data.seatNumbers || null,
            guestName: data.guest.first_name || 'Guest',
            guestId: data.guest.id,
            tableNumber: data.table,
            alreadyCheckedIn: data.guest?.status === 'Checked In',
            companionId: data.companionId || null,
            companionName: data.companionName || null,
            guestToken: data.guestToken || null,
            companionToken: data.companionToken || null,
            sessionToken: token,
          };
          setSeatInfo(seatData);
          setSuccessMode('checkin');
          setShowSuccess(true);
          setRestoredFromSession(true);
          // Persist session token
          try {
            localStorage.setItem('gp_guest_session_token', token);
          } catch {}
        } else {
          try {
            localStorage.removeItem('gp_guest_session_token');
          } catch {}
        }
      } catch {
        try {
          localStorage.removeItem('gp_guest_session_token');
        } catch {}
      } finally {
        if (!cancelled) setRestoringSession(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [IS_VNEXT, API_BASE_URL, showSuccess, restoredFromSession]);

  // Debounced fuzzy search suggestions
  useEffect(() => {
    if (!IS_DEV) return;
    const q = lastName.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const base = eventId ? `${API_BASE_URL}/events/${eventId}` : API_BASE_URL;
        const { data } = await axios.get(`${base}/search/guests`, { params: { q } });
        setSuggestions(data || []);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [lastName, eventId, API_BASE_URL]);

  const resetForm = () => {
    setLastName('');
    setType('Single');
    setGroupSize(3);
    setMessage('');
    setMessageType('');
    setMultipleGuests([]);
    setSelectedGuest(null);
    setShowGuestSelection(false);
    setShowSuccess(false);
    setSeatInfo(null);
    setShowCheckSeat(false);
    setSuggestions([]);
    setMenuSelections([]);
  };

  const handleSuggestionClick = (guest) => {
    setLastName(guest.last_name || guest.lastName || guest.name?.split(' ').slice(-1)[0] || '');
    setSuggestions([]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && lastName.trim() && !loading) {
      handleCheckIn();
    }
  };

  const handleGuestSelection = async (guestId) => {
    if (typeof guestId !== 'number') return;
    setLoading(true);
    setMessage('');
    try {
      const base = eventId ? `${API_BASE_URL}/events/${eventId}/public` : `${API_BASE_URL}/public`;
      const endpoint = selectionMode === 'checkSeat' ? 'check-seat/select' : 'checkin/select';
      const partySize = type === 'Couple' ? 2 : type === 'Single' ? 1 : groupSize;
      const { data } = await axios.post(`${base}/${endpoint}`, { guestId, type, partySize });
      const multiIsCheckedIn = data.alreadyCheckedIn || data.guest?.status === 'Checked In';
      if (selectionMode === 'checkSeat' && !multiIsCheckedIn) {
        setMessage('Please complete check-in first, then you can view your seat.');
        setMessageType('warn');
        setShowGuestSelection(false);
        return;
      }
      setSeatInfo({
        table: data.table,
        seat: data.seat,
        guestName: data.guest?.first_name || 'Guest',
        guestId: data.guest?.id,
        tableNumber: data.table,
        alreadyCheckedIn: selectionMode === 'checkSeat' ? multiIsCheckedIn : true,
        companionId: data.companionId || null,
        companionName: data.companionName || null,
        guestToken: data.guestToken || null,
        companionToken: data.companionToken || null,
        seatNumbers: data.seatNumbers || null,
      });
      setShowSuccess(true);
      setSuccessMode(selectionMode);
      setShowGuestSelection(false);
    } catch (e) {
      setMessage(e.response?.data?.message || 'Selection failed');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const getSelectionKey = () => {
    let key = null;
    try {
      key = localStorage.getItem('gp_menu_selection_key');
      if (!key) {
        key = 'msk_' + Math.random().toString(36).slice(2, 11);
        localStorage.setItem('gp_menu_selection_key', key);
      }
    } catch {}
    return key;
  };

  const toggleMenuItem = (itemId) => {
    setMenuSelections((prev) => {
      const set = new Set(prev);
      if (set.has(itemId)) set.delete(itemId);
      else set.add(itemId);
      return Array.from(set);
    });
  };

  useEffect(() => {
    if (!menuServiceEnabled || !menuSelections.length) return;
    const timer = setTimeout(async () => {
      try {
        const key = getSelectionKey();
        await fetch(`${SERVICE_BASE}/menu/selections/${encodeURIComponent(key)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ itemIds: menuSelections }),
        });
      } catch {}
    }, 600);
    return () => clearTimeout(timer);
  }, [menuSelections, menuServiceEnabled, SERVICE_BASE]);

  const handleCheckIn = async (token = undefined, retryCount = 0) => {
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 1000;

    if (IS_DEV) {
      console.log('[checkin] handler invoked', { lastName, type, eventId, retryCount });
    }

    if (!token && !lastName.trim()) {
      setMessage('Please enter your last name');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const base = eventId ? `${API_BASE_URL}/events/${eventId}/public` : `${API_BASE_URL}/public`;
      const url = `${base}/checkin`;
      const partySize = type === 'Couple' ? 2 : type === 'Single' ? 1 : groupSize;
      const payload = token ? { token } : { lastName: lastName.trim(), type, partySize };

      if (IS_DEV) {
        console.log('[checkin] POST', url, payload);
      }

      const response = await axios.post(url, payload);

      if (response.data?.multiple) {
        setMultipleGuests(response.data.guests || []);
        setSelectedGuest(null);
        setShowGuestSelection(true);
        setSelectionMode('checkin');
        setMessage('We found multiple guests with this name. Please select yourself:');
        setMessageType('info');
      } else {
        const seatData = {
          table: response.data.table,
          seat: response.data.seat,
          guestName: response.data.guest?.first_name || 'Guest',
          guestId: response.data.guest?.id,
          tableNumber: response.data.table,
          alreadyCheckedIn: true,
          companionId: response.data.companionId || null,
          companionName: response.data.companionName || null,
          guestToken: response.data.guestToken || null,
          companionToken: response.data.companionToken || null,
          sessionToken: response.data.session?.token || null,
        };
        setSeatInfo(seatData);
        if (response.data.session?.token) {
          try {
            localStorage.setItem('gp_guest_session_token', response.data.session.token);
          } catch {}
        }
        if (response.data.guest?.id) {
          setSelectedGuest({ id: response.data.guest.id, status: 'Checked In' });
        }
        // Claim pre-check-in menu selections
        try {
          if (response.data.guestToken) {
            const tmpKey = localStorage.getItem('gp_menu_selection_key');
            if (tmpKey) {
              const res = await fetch(`${SERVICE_BASE}/menu/selections/claim`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ key: tmpKey, guestToken: response.data.guestToken }),
              });
              const data = await res.json();
              if (data?.itemIds?.length) {
                setSeatInfo((prev) => ({ ...prev, existingSelections: data.itemIds }));
              }
            }
          }
        } catch {}
        setShowSuccess(true);
        setSuccessMode('checkin');
      }
    } catch (error) {
      if (IS_DEV) {
        console.error('[checkin] failed', error.response?.status, error.response?.data || error.message);
      }
      const status = error.response?.status;
      const backendMsg = (error.response?.data?.message || '').toLowerCase();

      if (status === 500 && retryCount < MAX_RETRIES) {
        setMessage('Server temporarily unavailable. Retrying...');
        setMessageType('info');
        setTimeout(() => handleCheckIn(token, retryCount + 1), RETRY_DELAY);
        return;
      }
      if (status === 500) {
        setMessage('Server error occurred. Please try again later or contact support.');
        setMessageType('error');
      } else if (status === 400 && backendMsg.includes('already checked in')) {
        setMessage('You are already checked in!');
        setMessageType('info');
      } else if (status === 404) {
        setMessage(
          backendMsg.includes('already checked in')
            ? 'You are already checked in! You can view your seat information below.'
            : 'Guest not found. Please check the spelling of your last name.'
        );
        setMessageType('error');
      } else {
        setMessage(error.response?.data?.message || 'An error occurred during check-in. Please try again.');
        setMessageType('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckSeat = async () => {
    if (!lastName.trim()) {
      setMessage('Please enter your last name');
      setMessageType('error');
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const base = eventId ? `${API_BASE_URL}/events/${eventId}/public` : `${API_BASE_URL}/public`;
      const { data } = await axios.post(`${base}/check-seat`, { lastName: lastName.trim(), type });
      if (data.multiple) {
        setMultipleGuests(data.guests || []);
        setSelectedGuest(null);
        setShowGuestSelection(true);
        setSelectionMode('checkSeat');
        setMessage('We found multiple guests with this name. Please select yourself:');
        setMessageType('info');
      } else {
        let isCheckedIn = data.alreadyCheckedIn || data.guest?.status === 'Checked In';
        if (!isCheckedIn && selectedGuest?.id && data.guest?.id === selectedGuest.id && selectedGuest.status === 'Checked In') {
          isCheckedIn = true;
        }
        if (data.guest?.id) {
          setSelectedGuest({ id: data.guest.id, status: isCheckedIn ? 'Checked In' : data.guest.status || 'Assigned' });
        }
        if (!isCheckedIn && !(data.table && data.seat)) {
          setMessage('Please complete check-in first, then you can view your seat.');
          setMessageType('warn');
          return;
        }
        setSeatInfo({
          table: data.table,
          seat: data.seat,
          guestName: data.guest?.first_name || 'Guest',
          guestId: data.guest?.id,
          tableNumber: data.table,
          alreadyCheckedIn: true,
          companionId: data.companionId || null,
          companionName: data.companionName || null,
          guestToken: data.guestToken || null,
          companionToken: data.companionToken || null,
        });
        setShowSuccess(true);
        setSuccessMode('checkSeat');
      }
    } catch (e) {
      const status = e.response?.status;
      const backendMsg = (e.response?.data?.message || '').toLowerCase();
      if (status === 404 && backendMsg.includes('already checked in')) {
        // Guest is already checked in, try to show their seat info
        setMessage('You are already checked in! You can view your seat information below.');
        setMessageType('info');
        // Try to fetch seat info anyway
        try {
          const base = eventId ? `${API_BASE_URL}/events/${eventId}/public` : `${API_BASE_URL}/public`;
          const { data } = await axios.post(`${base}/check-seat`, { lastName: lastName.trim(), type });
          if (data.table && data.seat) {
            setSeatInfo({
              table: data.table,
              seat: data.seat,
              guestName: data.guest?.first_name || 'Guest',
              guestId: data.guest?.id,
              tableNumber: data.table,
              alreadyCheckedIn: true,
              companionId: data.companionId || null,
              companionName: data.companionName || null,
              guestToken: data.guestToken || null,
              companionToken: data.companionToken || null,
            });
            setShowSuccess(true);
            setSuccessMode('checkSeat');
            return;
          }
        } catch (retryError) {
          // If retry fails, show the original message
        }
        setMessage('You are already checked in! Please contact staff if you need to view your seat information.');
        setMessageType('info');
      } else {
        setMessage(
          status === 404 && !backendMsg.includes('already checked in')
            ? 'You are not registered. Please check the spelling of your last name or contact the event organizer.'
            : status === 400 && backendMsg.includes('already checked in')
            ? 'You are already checked in'
            : e.response?.data?.message || 'Unable to find seat information'
        );
        setMessageType(status === 400 && backendMsg.includes('already checked in') ? 'info' : 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <SuccessModal
        seatInfo={seatInfo}
        eventConfig={eventConfig}
        resetForm={resetForm}
        message={message}
        messageType={messageType}
        setMessage={setMessage}
        setMessageType={setMessageType}
        menuServiceEnabled={menuServiceEnabled}
        SERVICE_BASE={SERVICE_BASE}
        API_BASE_URL={API_BASE_URL}
        successMode={successMode}
        seatMapVisibility={eventConfig?.seatMapVisibility || 'single'}
        onBack={() => {
          setShowSuccess(false);
          setSeatInfo(null);
          setMessage('');
          setMessageType('');
        }}
      />
    );
  }

  const atBaseScreen = !showGuestSelection && !showSuccess;
  return (
    <div className="check-in-card" style={{ position: 'relative' }}>
      <HealthBadge apiBase={API_BASE_URL} />
      {!atBaseScreen && (
        <button
          type="button"
          className="mobile-back-btn"
          onClick={() => {
            if (showGuestSelection) {
              setShowGuestSelection(false);
              setMultipleGuests([]);
              setSelectedGuest(null);
              setMessage('');
              setMessageType('');
            }
          }}
          aria-label="Go back"
        >
          ← Back
        </button>
      )}

      <div className="check-in-header">
        <div className="header-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="currentColor" />
            <path d="M12 6L12.5 7.5L14 8L12.5 8.5L12 10L11.5 8.5L10 8L11.5 7.5L12 6Z" fill="currentColor" opacity="0.6" />
          </svg>
        </div>
        <h1 className="check-in-title">Welcome to Our Event</h1>
        <p className="check-in-subtitle">Please check in to find your seat and access exclusive features</p>
      </div>

      {!showGuestSelection && (
        <div className="unified-form">
          <div className="mobile-layout" style={{ display: 'block' }}>
            <div className="form-group modern-input-group">
              <div className="input-wrapper">
                <input
                  id="lastName"
                  type="text"
                  className="form-input modern-input"
                  placeholder=" "
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  autoComplete="family-name"
                />
                <label htmlFor="lastName" className="floating-label">
                  <span className="label-icon">👤</span>
                  Your Last Name
                </label>
              </div>
            </div>

            <div className="form-group modern-input-group">
              <div className="input-wrapper">
                <select
                  id="attendanceType"
                  className="form-select modern-select"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  disabled={loading}
                >
                  <option value="Single">👤 Single</option>
                  <option value="Couple">👫 Couple</option>
                  <option value="Group">👨‍👩‍👧‍👦 Group (3+)</option>
                </select>
                <label htmlFor="attendanceType" className="floating-label">
                  <span className="label-icon">👥</span>
                  Reservation Type
                </label>
              </div>
            </div>

            {type === 'Group' && (
              <div className="form-group modern-input-group">
                <div className="input-wrapper">
                  <input
                    id="groupSize"
                    type="number"
                    min={3}
                    max={12}
                    value={groupSize}
                    onChange={(e) => setGroupSize(Math.max(3, Math.min(12, Number(e.target.value) || 3)))}
                    className="form-input modern-input"
                    disabled={loading}
                    placeholder=" "
                  />
                  <label htmlFor="groupSize" className="floating-label">
                    <span className="label-icon">👨‍👩‍👧‍👦</span>
                    Group Size
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="desktop-layout" style={{ display: 'none' }}>
            <div className="form-row">
              <div className="form-group modern-input-group" style={{ flex: '2 1 300px' }}>
                <div className="input-wrapper">
                  <input
                    id="lastNameDesktop"
                    type="text"
                    className="form-input modern-input"
                    placeholder=" "
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                    autoComplete="family-name"
                  />
                  <label htmlFor="lastNameDesktop" className="floating-label">
                    <span className="label-icon">👤</span>
                    Your Last Name
                  </label>
                </div>
              </div>

              <div className="form-group modern-input-group" style={{ flex: '1 1 180px' }}>
                <div className="input-wrapper">
                  <select
                    id="attendanceTypeDesktop"
                    className="form-select modern-select"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    disabled={loading}
                  >
                    <option value="Single">👤 Single</option>
                    <option value="Couple">👫 Couple</option>
                    <option value="Group">👨‍👩‍👧‍👦 Group (3+)</option>
                  </select>
                  <label htmlFor="attendanceTypeDesktop" className="floating-label">
                    <span className="label-icon">👥</span>
                    Reservation Type
                  </label>
                </div>
              </div>

              {type === 'Group' && (
                <div className="form-group modern-input-group" style={{ width: 110 }}>
                  <div className="input-wrapper">
                    <input
                      id="groupSizeDesktop"
                      type="number"
                      min={3}
                      max={12}
                      value={groupSize}
                      onChange={(e) => setGroupSize(Math.max(3, Math.min(12, Number(e.target.value) || 3)))}
                      className="form-input modern-input"
                      disabled={loading}
                      placeholder=" "
                    />
                    <label htmlFor="groupSizeDesktop" className="floating-label">
                      <span className="label-icon">👨‍👩‍👧‍👦</span>
                      Group Size
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-row actions-row" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <button
              className="btn btn-primary modern-btn primary-action"
              onClick={() => handleCheckIn()}
              disabled={loading || !lastName.trim()}
            >
              <span className="btn-icon">🎯</span>
              <span className="btn-text">{loading ? '⏳ Checking…' : 'Check Me In'}</span>
            </button>

            <button
              className="btn btn-secondary modern-btn secondary-action"
              onClick={handleCheckSeat}
              disabled={loading || !lastName.trim()}
            >
              <span className="btn-icon">📍</span>
              <span className="btn-text">{loading ? '⏳ Locating…' : 'View My Seat & More'}</span>
            </button>

            {menuServiceEnabled && !menuLoading && (
              <div className="feature-notice modern-notice">
                <span className="notice-icon">🍽️</span>
                <span className="notice-text">Menu ordering available after check-in</span>
              </div>
            )}

            {(message || lastName.trim()) && (
              <button
                className="btn btn-tertiary modern-btn tertiary-action"
                onClick={resetForm}
                disabled={loading}
              >
                <span className="btn-icon">🔄</span>
                <span className="btn-text">Start Over</span>
              </button>
            )}
          </div>
        </div>
      )}

      <div className="conditional-content">
        {showGuestSelection && (
          <div className="guest-selection modern-card">
            <div className="selection-header">
              <div className="header-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H5V21H19V9Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <h3>Please Select Yourself</h3>
              <p className="selection-subtitle">We found {multipleGuests.length} guests with that last name</p>
            </div>
            <div className="guest-list modern-list">
              {multipleGuests.map((guest) => (
                <div
                  key={guest.id}
                  className={`guest-item modern-item ${selectedGuest === guest.id ? 'selected' : ''}`}
                  onClick={() => setSelectedGuest(guest.id)}
                >
                  <div className="guest-info">
                    <div className="guest-name">{guest.name}</div>
                    <div className="guest-type">{guest.type === 'Couple' ? '👫 Couple' : '👤 Single'}</div>
                  </div>
                  <div className="selection-indicator">
                    {selectedGuest === guest.id && (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
                        <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="selection-actions">
              <button
                className="btn btn-primary modern-btn primary-action"
                onClick={() => handleGuestSelection(selectedGuest)}
                disabled={loading || typeof selectedGuest !== 'number'}
              >
                <span className="btn-icon">{loading ? '⏳' : '✅'}</span>
                <span className="btn-text">{loading ? 'Checking you in...' : "Yes, that's me!"}</span>
              </button>
              <button
                className="btn btn-tertiary modern-btn tertiary-action"
                onClick={resetForm}
                disabled={loading}
              >
                <span className="btn-icon">↩️</span>
                <span className="btn-text">Go Back</span>
              </button>
            </div>
          </div>
        )}

        {message && (
          <div className={`message modern-message ${messageType}`} role="alert" aria-live="assertive">
            <div className="message-content">
              <span className="message-icon">
                {messageType === 'success' && '🎉'}
                {messageType === 'error' && '⚠️'}
                {messageType === 'info' && 'ℹ️'}
                {messageType === 'warn' && '⚠️'}
              </span>
              <span className="message-text">{message}</span>
            </div>
            {pollWaitlist && (
              <div className="waitlist-info">
                {waitlistInfo?.position ? `Waitlist position: ${waitlistInfo.position}` : 'Checking waitlist position...'}
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="loading modern-loading">
            <div className="loading-spinner">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="18" stroke="currentColor" strokeWidth="2" opacity="0.2" />
                <circle cx="20" cy="20" r="18" stroke="currentColor" strokeWidth="2" strokeDasharray="28 28" strokeDashoffset="28">
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 20 20" to="360 20 20" dur="1s" repeatCount="indefinite" />
                </circle>
              </svg>
            </div>
            <div className="loading-text">Finding your reservation...</div>
          </div>
        )}
      </div>
    </div>
  );
}
